import api from '../services/api';

// Websites cannot push attachments into a mail app via mailto (browser security).
// Instead we build a real email file (.eml) with the document attached and
// "X-Unsent: 1" so Outlook opens it as an editable DRAFT with the attachment
// already in place — the user just reviews and presses Send.

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const buildEml = ({ to, subject, body, attachments }) => {
  const boundary = `----=_UL_${Date.now()}`;
  let eml = 'X-Unsent: 1\r\n';
  if (to) eml += `To: ${to}\r\n`;
  eml += `Subject: ${subject}\r\nMIME-Version: 1.0\r\n`;
  eml += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n`;
  eml += `--${boundary}\r\nContent-Type: text/plain; charset=utf-8\r\nContent-Transfer-Encoding: 8bit\r\n\r\n${body}\r\n\r\n`;
  attachments.forEach(({ filename, mime, base64 }) => {
    eml += `--${boundary}\r\nContent-Type: ${mime}; name="${filename}"\r\n`;
    eml += `Content-Transfer-Encoding: base64\r\nContent-Disposition: attachment; filename="${filename}"\r\n\r\n`;
    eml += base64.replace(/(.{76})/g, '$1\r\n') + '\r\n';
  });
  eml += `--${boundary}--`;
  return eml;
};

const triggerDownload = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// attachmentBlob: a Blob to attach directly (e.g. frontend-generated PDF)
// pdfPath: backend path to fetch the attachment from (used when no blob given)
export const openEmailWithAttachment = async ({ to = '', subject = '', body = '', pdfPath, filename, attachmentBlob, attachmentMime = 'application/pdf' }) => {
  let blob = attachmentBlob || null;
  if (!blob && pdfPath) {
    try {
      const res = await api.get(pdfPath, { responseType: 'blob' });
      blob = new Blob([res.data], { type: attachmentMime });
    } catch (e) {
      console.error('Attachment fetch failed', e);
      alert('Could not generate the attachment.');
      return;
    }
  }
  const attachments = [];
  if (blob) {
    attachments.push({ filename: filename || 'document.pdf', mime: attachmentMime, base64: await blobToBase64(blob) });
  }
  const eml = buildEml({ to, subject, body, attachments });
  const emlName = `${(filename || subject || 'email').replace(/\.(pdf|html?)$/i, '')}.eml`;
  triggerDownload(new Blob([eml], { type: 'message/rfc822' }), emlName);
  alert(`Email draft "${emlName}" downloaded with the attachment inside.\n\nDouble-click the downloaded file — it opens in Outlook as a ready-to-send email with "${filename}" already attached. Just press Send.`);
};
